import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

interface AddSubjectModalProps {
  visible: boolean;
  courseId?: string;
  courseName?: string;
  editingSubject?: {
    subject_id: string;
    subject_name: string;
    course_id: string;
  } | null;
  onHide: () => void;
  onSuccess?: () => void;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

const AddSubjectModal: React.FC<AddSubjectModalProps> = ({
  visible,
  courseId,
  courseName,
  editingSubject,
  onHide,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [editingCourseName, setEditingCourseName] = useState<string>('');

  const [formData, setFormData] = useState({
    subject_name: '',
    course_id: courseId || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = !!editingSubject;
  const isCoursePreselected = !!courseId && !!courseName;
  const isCourseFixed = isEditMode || isCoursePreselected;

  useEffect(() => {
    if (visible) {
      if (editingSubject) {
        // Populate form with editing subject data
        setFormData({
          subject_name: editingSubject.subject_name,
          course_id: editingSubject.course_id
        });
        // Fetch course name for editing mode
        fetchCourseNameForEdit(editingSubject.course_id);
      } else if (courseId) {
        // Pre-select course if provided
        setFormData({
          subject_name: '',
          course_id: courseId
        });
      } else {
        // Reset form for new subject
        setFormData({
          subject_name: '',
          course_id: ''
        });
      }

      // Fetch courses only if not in edit mode and no course preselected
      if (!isEditMode && !isCoursePreselected) {
        fetchCourses();
      }
    }
  }, [visible, editingSubject, courseId, isCoursePreselected, isEditMode]);

  const fetchCourses = async () => {
    try {
      setCoursesLoading(true);
      setError(null);

      const response = await apiClient.get('/v1/courses?limit=100');

      if (response.data.success) {
        const activeCourses = response.data.data.filter(
          (course: Course) => course.status === 'active'
        );
        setCourses(activeCourses);
      } else {
        setError('Failed to load courses');
      }
    } catch (err: any) {
      console.error('Failed to fetch courses:', err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to load courses';
      setError(errorMessage);
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchCourseNameForEdit = async (courseId: string) => {
    try {
      const response = await apiClient.get(`/v1/courses?limit=100`);
      if (response.data.success) {
        const course = response.data.data.find((c: Course) => c.course_id === courseId);
        if (course) {
          setEditingCourseName(`${course.name} - ${course.title}`);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch course name:', err);
    }
  };

  const courseOptions: DropdownOption[] = courses.map(course => ({
    label: `${course.name} - ${course.title}`,
    value: course.course_id
  }));

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.subject_name.trim()) {
      newErrors.subject_name = 'Subject name is required';
    }

    if (!formData.course_id) {
      newErrors.course_id = 'Course selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill all required fields correctly');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        subject_name: formData.subject_name.trim(),
        course_id: formData.course_id
      };

      let response;

      if (isEditMode) {
        // Update existing subject
        response = await apiClient.put(`/v1/subjects/${editingSubject.subject_id}`, payload);
      } else {
        // Create new subject
        response = await apiClient.post('/v1/subjects', payload);
      }

      if (response.data.success) {
        handleClose();
        if (onSuccess) onSuccess();
      } else {
        setError(response.data.message || `Failed to ${isEditMode ? 'update' : 'create'} subject`);
      }
    } catch (err: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} subject:`, err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        `Failed to ${isEditMode ? 'update' : 'create'} subject`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      subject_name: '',
      course_id: courseId || ''
    });
    setErrors({});
    setError(null);
    setEditingCourseName('');
    onHide();
  };

  // Get display name for course when it's fixed
  const getCourseName = () => {
    if (courseName) return courseName;
    if (isEditMode && editingCourseName) return editingCourseName;
    return 'Loading...';
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: {
          style: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }
        }
      }}
    >
      <div>
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              background: 'rgba(212, 241, 126, 0.1)',
              borderRadius: '50%',
              filter: 'blur(40px)'
            }}
          />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                background: 'rgba(212, 241, 126, 0.2)',
                borderRadius: '16px',
                marginBottom: '8px',
                fontSize: '0.65rem',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}
            >
              Subject Management
            </div>
            <h2
              style={{
                margin: '0 0 6px 0',
                fontSize: '1.4rem',
                fontWeight: '700',
                color: 'white'
              }}
            >
              {isEditMode 
                ? 'Edit Subject' 
                : isCoursePreselected 
                  ? `Add Subject to ${courseName}` 
                  : 'Add New Subject'}
            </h2>
            <p
              style={{
                margin: '0',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '0.8rem'
              }}
            >
              {isEditMode
                ? 'Update the subject information'
                : isCoursePreselected
                  ? 'Create a new subject under the selected course'
                  : 'Select a course and create a new subject'}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <Message
            severity="error"
            text={error}
            style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
          />
        )}

        {/* Loading courses message */}
        {coursesLoading && !isCourseFixed && (
          <Message
            severity="info"
            text="Loading available courses..."
            style={{ marginBottom: '20px', fontSize: '0.9rem' }}
          />
        )}

        {/* Form Content */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #f0f0f0'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Course Field - Fixed when editing or preselected */}
            {isCourseFixed ? (
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#1a1a1a'
                  }}
                >
                  Course <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <div
                  style={{
                    padding: '12px 16px',
                    background: '#f5f5f5',
                    borderRadius: '10px',
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <i className="pi pi-graduation-cap" style={{ color: '#0f3d3e', fontSize: '1rem' }} />
                  <span
                    style={{
                      fontSize: '0.9rem',
                      color: '#1a1a1a',
                      fontWeight: '500'
                    }}
                  >
                    {getCourseName()}
                  </span>
                  {isEditMode && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        padding: '4px 8px',
                        background: 'rgba(15, 61, 62, 0.1)',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        color: '#0f3d3e',
                        fontWeight: '600'
                      }}
                    >
                      Cannot be changed
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <GlobalDropdownField
                label="Course"
                required
                value={formData.course_id}
                onChange={(val) => handleInputChange('course_id', val)}
                options={courseOptions}
                placeholder="Select a course"
                icon="pi pi-graduation-cap"
                error={errors.course_id}
                disabled={coursesLoading || courses.length === 0}
              />
            )}

            {/* Subject Name */}
            <GlobalInputField
              label="Subject Name"
              required
              value={formData.subject_name}
              onChange={(e) => handleInputChange('subject_name', e.target.value)}
              placeholder="e.g., Calculus I, Organic Chemistry, Web Development"
              icon="pi pi-book"
              error={errors.subject_name}
            />
          </div>

          {/* Warning when no courses available in general mode */}
          {!isCourseFixed && courses.length === 0 && !coursesLoading && (
            <Message
              severity="warn"
              text="No active courses available. Please create a course first."
              style={{ marginTop: '16px', fontSize: '0.9rem' }}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}
        >
          <GlobalButton
            label="Cancel"
            variant="outline"
            icon="pi pi-times"
            onClick={handleClose}
            disabled={loading}
          />

          <GlobalButton
            label={loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Subject' : 'Create Subject')}
            variant="primary"
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleSubmit}
            disabled={
              loading ||
              coursesLoading ||
              (!isCourseFixed && courses.length === 0)
            }
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default AddSubjectModal;